import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';

import { DashboardTemplate } from 'modules/v2/templates';
import { Button, Spinner } from 'modules/v2/common/AtomicDesign/atoms';
import { getModOrders } from 'modules/api/digitalServices/mailOnDemand';
import {
  getRouteMailOnDemandNew,
  getRouteMailOnDemandOrder,
  getRouteMailOnDemandResume,
} from 'modules/v2/routes/navigation';

import StatusChip from '../components/StatusChip';
import strings, { formatMessage } from '../strings';
import { RESUMABLE_STATUSES } from '../constants';
import { formatCredits, formatDate } from '../utils';

export const MailOnDemandOrdersList = () => {
  const history = useHistory();
  const { data: orders, isLoading, isError, refetch } = useQuery('modOrders', getModOrders);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <Spinner />
        <span className="text-neutral-500">{strings.orders.loading}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3 py-6">
        <p>{strings.orders.loadError}</p>
        <Button color="outlined" onClick={() => refetch()}>
          {strings.common.retry}
        </Button>
      </div>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="py-12 text-center" data-testid="mod-orders-empty">
        <h3 className="mb-2 text-lg font-semibold">{strings.orders.emptyTitle}</h3>
        <p className="mb-6 text-neutral-500">{strings.orders.emptyBody}</p>
        <Button onClick={() => history.push(getRouteMailOnDemandNew())}>
          {strings.orders.newOrder}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex justify-end">
        <Button onClick={() => history.push(getRouteMailOnDemandNew())} data-testid="mod-new-order">
          {strings.orders.newOrder}
        </Button>
      </div>

      <ul className="flex flex-col gap-3">
        {orders.map((order) => {
          const isResumable = RESUMABLE_STATUSES.includes(order.status);

          return (
            <li
              key={order._id}
              data-testid={`mod-order-row-${order._id}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-white p-4"
            >
              <div className="flex min-w-0 flex-col gap-1">
                <span className="truncate font-medium">{order.asset?.title}</span>
                <span className="text-xs text-neutral-500">
                  {formatMessage(strings.orders.createdAt, { date: formatDate(order.createdAt) })}
                  {order.resolution?.mailableCount !== undefined && (
                    <>
                      {' · '}
                      {formatMessage(strings.orders.recipientsCount, {
                        count: formatCredits(order.resolution.mailableCount),
                      })}
                    </>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <StatusChip status={order.status} />
                {isResumable ? (
                  <Button
                    size="sm"
                    onClick={() => history.push(getRouteMailOnDemandResume(order._id))}
                    data-testid={`mod-continue-order-${order._id}`}
                  >
                    {strings.orders.continueOrder}
                  </Button>
                ) : (
                  <Button
                    color="outlined"
                    size="sm"
                    onClick={() => history.push(getRouteMailOnDemandOrder(order._id))}
                  >
                    {strings.orders.viewOrder}
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const MailOnDemandOrdersPage = () => (
  <DashboardTemplate hasSidebar title={strings.orders.pageTitle}>
    <MailOnDemandOrdersList />
  </DashboardTemplate>
);

export default MailOnDemandOrdersPage;
